import { DailyQuestionFigure } from '@backrs_ui/grow-ui/src/redux/satActBooster/satActBooster.types';

/**
 * A bold figure caption on its own line — `**Figure 2**` — marking where a figure sat on
 * the page. Table captions look the same but their tables are already markdown.
 */
const FIGURE_MARKER_SOURCE = /^\s*\*\*(Figure\s+\d+[A-Za-z]?)\*\*\s*$/;

/** Passage prose, or a figure standing where the booklet printed it. */
export type PassageSegment =
  | Readonly<{ kind: 'markdown'; content: string }>
  | Readonly<{ kind: 'figure'; figure: DailyQuestionFigure }>;

export type PassageLayout = Readonly<{
  segments: readonly PassageSegment[];
  /** Figures no marker claimed. They stack after the passage rather than being dropped. */
  trailing: readonly DailyQuestionFigure[];
}>;

const normalize = (label: string) => label.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Place a question's figures where its passage prints their captions.
 *
 * Matched by label, never by position: figures can arrive in an order that no longer
 * matches their captions, and placing by position looks normal while being wrong.
 */
export const layOutPassageFigures = (
  passage: string,
  figures: readonly DailyQuestionFigure[],
): PassageLayout => {
  const byLabel = new Map<string, DailyQuestionFigure>();

  for (const figure of figures) {
    if (figure.label) byLabel.set(normalize(figure.label), figure);
  }

  const segments: PassageSegment[] = [];
  const claimed = new Set<DailyQuestionFigure>();
  let cursor = 0;

  // Its own instance: a shared regex carries `lastIndex` across calls and skips markers.
  const marker = new RegExp(FIGURE_MARKER_SOURCE.source, 'gm');
  let match = marker.exec(passage);

  while (match !== null) {
    const figure = byLabel.get(normalize(match[1]));

    if (figure && !claimed.has(figure)) {
      const before = passage.slice(cursor, match.index);

      if (before.trim()) segments.push({ kind: 'markdown', content: before });

      segments.push({ kind: 'figure', figure });
      claimed.add(figure);
      cursor = match.index + match[0].length;
    }

    match = marker.exec(passage);
  }

  const rest = passage.slice(cursor);

  if (rest.trim()) segments.push({ kind: 'markdown', content: rest });

  return { segments, trailing: figures.filter((figure) => !claimed.has(figure)) };
};
